import React, { useEffect, useState } from "react";
import { FaDatabase, FaPlug } from "react-icons/fa";
import { clientConnectorActions } from "../../actions/clientConnector.actions";
import Title from "../common/Title";
import EnhancedGrid from "../common/EnhancedGrid";
import Utils from "../../utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ConnectorGridFilter from "./ConnectorGridFilter";

import GridHelper from "../helpers/gridHelper";
import { MdDelete, MdEdit, MdGppBad, MdGppGood } from "react-icons/md";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import ConfirmModal from "../common/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";

// OnPrem connectors run a locally installed agent; their Test Connection is an async round-trip
// and, together with the Last Test status and Push Update, lives on the connector's edit page.
// The grid's synchronous "Test Auth" applies only to cloud connectors.
const ONPREM_SYSTEMS = ["OnPremiseSQL", "OnPremiseFiles"];
const isOnPrem = (connector) =>
  !!connector && ONPREM_SYSTEMS.includes(connector.system);

function ConnectorGrid(props) {
  const { hideTitle, onAddConnector, onEditConnector } = props;
  const { hasPermission } = usePermissions(props.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clientConnectorReducer = useSelector((state) => state.clientConnector);
  const [testConnectorAuth, setTestConnectorAuth] = useState({});
  const [
    confirmTestConnectorAuthModalShow,
    setConfirmTestConnectorAuthModalShow,
  ] = useState(false);
  const [testAuthResultModalShow, setTestAuthResultModalShow] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [confirmDeleteModalShow, setConfirmDeleteModalShow] = useState(false);

  const filterInitialState = {
    description: "",
    system: "",
    connectorId: "",
  };
  const [filter, setFilter] = useState(filterInitialState);

  const connectorFormattedName = (connector) => {
    return `${connector.connectorNumber} - ${connector.description}`;
  };

  const buildQueryParams = () => {
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;
    return queryParams;
  };

  useEffect(() => {
    dispatch(clientConnectorActions.get(buildQueryParams()));

    // Initialize filter from URL
    let queryParams = Utils.getQueryStringParams(location);
    setFilter({
      system: queryParams["system"],
      description: queryParams["description"],
    });

    // eslint-disable-next-line
  }, [location]);

  useEffect(() => {
    if (clientConnectorReducer.deleteSuccess) {
      setSelectedConnector(null);
      dispatch(clientConnectorActions.get(buildQueryParams()));
    }
    // eslint-disable-next-line
  }, [clientConnectorReducer.deleteSuccess]);

  const handleDelete = () => {
    setConfirmDeleteModalShow(false);
    dispatch(
      clientConnectorActions.remove({
        connectorId: selectedConnector.connectorId,
        clientId: selectedConnector.clientId,
        clientGuid: selectedConnector.clientGuid,
      })
    );
  };

  const handleClearFilters = () => {
    GridHelper.handleSearch(filterInitialState, location, navigate);
  };

  // Cloud connectors pre-authenticate synchronously; OnPrem connectors are tested from their
  // edit page (async agent round-trip), so the grid button is disabled for them.
  const handleTestConnectorAuth = () => {
    setConfirmTestConnectorAuthModalShow(false);
    setTestAuthResultModalShow(true);

    dispatch(
      clientConnectorActions.testConnectorAuth({
        connectorId: testConnectorAuth.connectorNumber,
        clientId: testConnectorAuth.clientId,
        clientGuid: testConnectorAuth.clientGuid,
      })
    );
  };

  const handleCloseTestResultModal = () => {
    setTestAuthResultModalShow(false);
  };

  const data = clientConnectorReducer.paginatedResult?.data;

  let displayColumns = [
    "connectorNumber",
    "description",
    "system",
    "loginEndpoint",
    "companyId",
  ];
  if (props.user.isSuperUser) displayColumns.push("clientName");

  const gridData = data ?? [];

  // Grid Test Auth is for cloud connectors only.
  const canTestSelected = !!selectedConnector && !isOnPrem(selectedConnector);

  // Check if user has permission to view connectors
  if (!hasPermission("connectors.view")) {
    return (
      <>
        {!hideTitle && <Title text="My Connectors" icon={<FaDatabase className="mb-1" />} />}
        <Alert variant="danger">
          You do not have permission to view connectors. Please contact your administrator.
        </Alert>
      </>
    );
  }

  // Connectors are scoped to a client — the left menu already disables this page's nav link
  // without one selected, but this page is still directly reachable (bookmark, back button,
  // clearing the client selector while already here), so gate the content itself too instead
  // of rendering filters/actions/grid against no client.
  if (!props.user.clientId) {
    return (
      <>
        {!hideTitle && <Title text="My Connectors" icon={<FaDatabase className="mb-1" />} />}
        <Alert variant="info">
          Select a client from the menu at the top of the page to see their connectors.
        </Alert>
      </>
    );
  }

  return (
    <>
      {!hideTitle && <Title text="My Connectors" icon={<FaDatabase className="mb-1" />} />}

      <ConnectorGridFilter
        filter={filter}
        onClearFilters={handleClearFilters}
      />

      {/* Action Buttons Bar */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded">

        {(props.user.isAdmin || props.user.isSuperUser) && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => (onAddConnector ? onAddConnector() : navigate("add"))}
          >
            Add Connector
          </Button>
        )}

        <div className="d-flex gap-2" style={{
             opacity: selectedConnector ? 1 : 0.6,
             transition: 'opacity 0.3s ease'
           }}>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!selectedConnector || !hasPermission("connectors.edit")}
            onClick={() => {
              if (selectedConnector) {
                if (onEditConnector) {
                  onEditConnector(selectedConnector.connectorId);
                } else {
                  navigate(`edit?connectorId=${selectedConnector.connectorId}`);
                }
              }
            }}
            title={!hasPermission("connectors.edit") ? "You do not have permission to edit connectors" : ""}
          >
            <MdEdit className="mb-1 me-2" />
            Edit
          </Button>

          <Button
            variant="outline-primary"
            size="sm"
            disabled={!canTestSelected}
            onClick={() => {
              if (canTestSelected) {
                setTestConnectorAuth(selectedConnector);
                setConfirmTestConnectorAuthModalShow(true);
              }
            }}
            title={
              isOnPrem(selectedConnector)
                ? "Use Test Connection on the connector's edit page for OnPrem connectors"
                : ""
            }
          >
            <MdGppGood className="mb-1 me-2" />
            Test Auth
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            disabled={!selectedConnector || !hasPermission("connectors.delete") || clientConnectorReducer.deleting}
            onClick={() => {
              if (selectedConnector) setConfirmDeleteModalShow(true);
            }}
            title={!hasPermission("connectors.delete") ? "You do not have permission to delete connectors" : ""}
          >
            {clientConnectorReducer.deleting ? (
              <Spinner size="sm" className="me-2" />
            ) : (
              <MdDelete className="mb-1 me-2" />
            )}
            Delete
          </Button>
        </div>

        {selectedConnector && (
          <div className="ms-auto align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedConnector.connectorNumber} - {selectedConnector.description}</strong>
            </small>
          </div>
        )}
      </div>

      {clientConnectorReducer.deleteError && (
        <Alert variant="danger">
          This connector cannot be deleted because it is assigned to one or more runbooks. Remove it from those runbooks first.
        </Alert>
      )}

      <EnhancedGrid
        gridId="connector"
        data={gridData}
        displayColumns={displayColumns}
        emptyState={{
          icon: <FaPlug size={80} className="text-muted mb-3" />,
          message: "No connectors found",
          subtitle: "Add a connector to integrate with external systems.",
          action: (props.user.isAdmin || props.user.isSuperUser) ? (
            <Button variant="primary" size="sm" onClick={() => (onAddConnector ? onAddConnector() : navigate("add"))}>
              Add Connector
            </Button>
          ) : undefined,
        }}
        idColumn={"connectorId"}
        selectedRow={selectedConnector}
        onRowClick={(connector) => setSelectedConnector(connector)}
        pageNumber={(clientConnectorReducer.paginatedResult?.pageNumber ?? 1) - 1}
        totalPages={clientConnectorReducer.paginatedResult?.totalPages ?? 0}
        loading={clientConnectorReducer.loading}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
      />

      <ConfirmModal
        show={confirmDeleteModalShow}
        title="Delete Connector"
        question={
          selectedConnector
            ? `Are you sure you want to delete connector ${connectorFormattedName(selectedConnector)}? This action cannot be undone.`
            : ""
        }
        noButtonText="Cancel"
        yesButtonText="Delete"
        handleClose={() => setConfirmDeleteModalShow(false)}
        handleConfirm={handleDelete}
      />

      <ConfirmModal
        show={confirmTestConnectorAuthModalShow}
        title={`Test Connector Auth - ${testConnectorAuth.connectorNumber}`}
        question={`Are you sure you want to test connector auth for ${connectorFormattedName(
          testConnectorAuth
        )}?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => {
          setConfirmTestConnectorAuthModalShow(false);
        }}
        handleConfirm={() => {
          handleTestConnectorAuth();
        }}
      />

      {testConnectorAuth && (
        <Modal
          show={testAuthResultModalShow}
          onHide={handleCloseTestResultModal}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Test Connector Auth - {testConnectorAuth.connectorNumber}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {clientConnectorReducer.loadingTestAuth && (
              <>
                Testing Connector Auth {connectorFormattedName(testConnectorAuth)}
                ...
                <Spinner
                  className="float-end"
                  size="sm"
                  variant="primary"
                ></Spinner>
                <div className="clearfix"></div>
              </>
            )}

            {!clientConnectorReducer.loadingTestAuth &&
              clientConnectorReducer.testAuthResponse && (
                <>
                  {!clientConnectorReducer.testAuthResponse.hasErrors ? (
                    <div className="text-success">
                      <MdGppGood className="me-2 mb-1" />
                      Connector Auth successful!
                    </div>
                  ) : (
                    <div className="text-danger">
                      <MdGppBad className="me-2 mb-1" />
                      {clientConnectorReducer.testAuthResponse.message}
                    </div>
                  )}
                </>
              )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseTestResultModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

export default ConnectorGrid;
